import { useReactiveVar } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import { clearCache } from '@/shared/api';
import { ROUTES } from '@/shared/config';
import { _refreshTokenVar, _relayingPartyIdVar, _roleTypeVar, _tokenVar, session } from '@/shared/lib/session';
import type { AccountsMonitoringDeviceType } from '@/shared/lib/types/remotePatientMonitoring';
import ls from '@/shared/lib/util/localstorage';
import { Stack } from '@/shared/lib/util/stack';
import { useRpmWorkflowStore } from '@/shared/model';
import { useDataGridPreferencesStore } from '@/shared/model/DataGridPreferencesStore';

export type RecentPatientType = {
  truentityId: string;
  name: string;
  rpmStatus: string | null;
  accountsMonitoringDevices?: AccountsMonitoringDeviceType[];
};

const useToken = () => {
  const navigate = useNavigate();
  const token = useReactiveVar(_tokenVar);
  const refreshToken = useReactiveVar(_refreshTokenVar);
  const relayingPartyId = useReactiveVar(_relayingPartyIdVar);
  const roleType = useReactiveVar(_roleTypeVar);

  const { stopMedTaskTrackingFn } = useRpmWorkflowStore();

  const getRecentPatients = (): RecentPatientType[] => {
    const key = 'recent-patients-' + token;
    const stack = new Stack(3, key);
    return stack.getItems() as RecentPatientType[];
  };

  const addNewRecentPatient = (data: RecentPatientType) => {
    const key = 'recent-patients-' + token;
    const stack = new Stack(3, key);
    stack.push(data);
  };

  const handleLogout = (timedOut = false) => {
    session.logout(timedOut);
    stopMedTaskTrackingFn?.();
    clearCache();
    useDataGridPreferencesStore.getState().clear();
    ls.clear();
    navigate(ROUTES.LOGIN);
  };

  return {
    setToken: session.setToken,
    setTokenAndRefreshToken: session.setTokenAndRefreshToken,
    setRelayingPartyId: session.setRelayingPartyId,
    setRoleType: session.setRoleType,
    logout: session.logout,
    token,
    refreshToken,
    relayingPartyId,
    roleType,
    getIsIdle: session.getIsIdle,
    handleLogout,
    getRecentPatients,
    addNewRecentPatient
  };
};

export default useToken;
